import { guardAdminOnly } from "@/lib/auth/routeGuards"
import { useState, useEffect } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { createFileRoute } from '@tanstack/react-router'
import {
  Sidebar,
  AttendanceDashboard,
  SessionManagement,
  StatusOverview,
  Reports,
  TeacherAbsence,
  SessionGenerator,
  Profile,
  StudentSessionsView,
} from '@/components/adminPage/attendance'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Trash2 } from 'lucide-react'
import { useAdminStore } from '@/store/adminStore'
import { AttendanceAdminTour } from '@/tour/admin/attendanceAdminTour'
import { SessionManagementTour } from '@/tour/admin/sessionManagementTour'
import { SessionGeneratorTour } from '@/tour/admin/sessionGeneratorTour'
import { StatusOverviewTour } from '@/tour/admin/statusOverviewTour'
import { StudentSessionsTour } from '@/tour/admin/studentSessionsTour'
import { useTranslation } from 'react-i18next'
import { useUser, useUserStore } from '@/hooks/useUser'
import { useToast } from '@/components/ui/use-toast'
import { deleteEstablishmentAttendance } from '@/services/attendanceService'

interface SearchParams {
  component?: string
}

export const Route = createFileRoute('/admin/attendance')({
  beforeLoad: guardAdminOnly,
  component: AttendanceRoute,
  validateSearch: (search: Record<string, unknown>) => {
    return { component: search.component as string }
  },
})

function AttendanceRoute() {
  const [activeComponent, setActiveComponent] = useState('Dashboard')
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isFetchingUser, ] = useState(false)
  const [sessionManagementKey, ] = useState(0)
  const navigate = useNavigate()
  const { isLoading: isUserLoading } = useUser()
  const { toast } = useToast()
  const search = useSearch({ from: '/admin/attendance' }) as SearchParams
  const { 
    attendanceTour,
    sessionManagementTour,
    sessionGeneratorTour,
    statusOverviewTour,
    studentSessionsTour,
  } = useAdminStore()
  const { t } = useTranslation('attendance')
  
  // Set active component based on search parameter
  useEffect(() => {
    if (search.component) {
      setActiveComponent(search.component)
    } else {
      // If no component specified in URL, show Dashboard by default
      setActiveComponent('Dashboard')
      // Update URL to reflect the default component
      navigate({ 
        to: '/admin/attendance',
        search: (prev) => ({ ...prev, component: 'Dashboard' })
      })
    }
  }, [search.component, navigate])

  // Ensure user data is loaded on mount
  useEffect(() => {
    // Always try to fetch/refresh user data to ensure establishmentId is available
    if (!isUserLoading) {
      const currentUser = useUserStore.getState().user
      // If no user or no establishmentId, fetch from API
      if (!currentUser || !currentUser.establishmentId) {
        useUserStore.getState().fetchUser()
      }
    }
  }, [isUserLoading])

  // Handle delete attendance
  const handleDeleteAttendance = async () => {
    const currentUser = useUserStore.getState().user
    
    if (!currentUser?.establishmentId) {
      toast({
        title: t('route.attendance.toast.deleteError.title'),
        description: t('route.attendance.toast.deleteError.establishmentIdNotFound'),
        variant: 'destructive',
      })
      setIsDeleteDialogOpen(false)
      return
    }

    setIsDeleting(true)
    try {
      await deleteEstablishmentAttendance(currentUser.establishmentId)
      
      toast({
        title: t('route.attendance.toast.deleteSuccess.title'),
        description: t('route.attendance.toast.deleteSuccess.description'),
        variant: 'default',
      })
      
      setIsDeleteDialogOpen(false)
      
      // Optionally refresh the page or refetch data
      // You might want to invalidate queries here if using React Query
      window.location.reload()
    } catch (error) {
      // console.error('Error deleting attendance:', error)
      toast({
        title: t('route.attendance.toast.deleteError.title'),
        description: t('route.attendance.toast.deleteError.description'),
        variant: 'destructive',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  // Map of components to render based on active component
  const componentsMap = {
    Dashboard: <AttendanceDashboard />,
    SessionManagement: <SessionManagement key={sessionManagementKey} />,
    StatusOverview: <StatusOverview />,
    Reports: <Reports />,
    TeacherAbsence: <TeacherAbsence />,
    SessionGenerator: <SessionGenerator />,
    Profile: <Profile />,
    StudentSessions: <StudentSessionsView />,
  }

  // Function to go back to the previous route
  function goBack() {
    navigate({ to: '/admin' })
  }

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Sidebar */}
      <Sidebar
        setActiveComponent={setActiveComponent}
        goBack={goBack}
        activeComponent={activeComponent}
      />

      {/* Main View - Dynamically rendered components */}
      <div className="flex-1 p-6 overflow-auto">
        {activeComponent !== 'Profile' && (
          <div className="mb-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">{t('route.attendance.title')}</h1>
              <p className="text-muted-foreground">
                {t('route.attendance.subtitle')}
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                onClick={() => setIsDeleteDialogOpen(true)}
                className="flex items-center gap-2"
                disabled={isDeleting || isFetchingUser}
              >
                <Trash2 className="h-4 w-4" />
                {t('route.attendance.deleteAllAttendanceButton.label')}
              </Button>
            </div>
          </div>
        )}
        {componentsMap[activeComponent as keyof typeof componentsMap]}

        {/* Delete Confirmation Dialog */}
        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('route.attendance.deleteDialog.title')}</DialogTitle>
              <DialogDescription>
                {t('route.attendance.deleteDialog.description')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsDeleteDialogOpen(false)}
                disabled={isDeleting}
              >
                {t('route.attendance.deleteDialog.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteAttendance}
                disabled={isDeleting}
              >
                {isDeleting ? t('route.attendance.deleteDialog.deleting') : t('route.attendance.deleteDialog.delete')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Render appropriate tour based on active component */}
        {activeComponent === 'Dashboard' && attendanceTour.isActive && (
          <AttendanceAdminTour />
        )}
        {activeComponent === 'SessionManagement' && sessionManagementTour.isActive && (
          <SessionManagementTour />
        )}
        {activeComponent === 'SessionGenerator' && sessionGeneratorTour.isActive && (
          <SessionGeneratorTour />
        )}
        {activeComponent === 'StatusOverview' && statusOverviewTour.isActive && (
          <StatusOverviewTour />
        )}
        {activeComponent === 'StudentSessions' && studentSessionsTour.isActive && (
          <StudentSessionsTour />
        )}
      </div>
    </div>
  )
}
